import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, Truck } from 'lucide-react';
import {
  DELIVERY_SLOTS,
  DELIVERY_STATUSES,
  formatMoney,
  formatQty,
  lineTotal,
  NON_ACCOUNT_METHODS,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  type CurrencyCode,
  type DeliveryStatus,
  type OrderStatus,
  type PaymentMethod,
} from '@tengesa/shared';
import { api } from '../../lib/api';
import { uuid } from '../../lib/uuid';
import { addDays, todayLocal } from '../../lib/dates';
import type { ProductDto } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextArea, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { MoneyInput, ProductPicker, QtyInput } from '../../ui/inputs';
import { CustomerPicker, type PickedCustomer } from '../customers/CustomerPicker';
import { DeliveryDialog } from '../deliveries/DeliveriesPage';

interface OrderRow {
  id: string;
  number: string;
  status: OrderStatus;
  createdAt: string;
  dueDate: string | null;
  customerName: string;
  branchName: string;
  itemCount: number;
  totalCents: number;
  paidCents: number;
  balanceCents: number;
}
export interface OrderDetail extends Omit<OrderRow, 'itemCount'> {
  branchId: string;
  customerId: string;
  customerPhone: string | null;
  customerAddress: string | null;
  notes: string | null;
  cancelReason: string | null;
  createdByName: string | null;
  saleId: string | null;
  saleNumber: string | null;
  refundedCents: number;
  forfeitedCents: number;
  lines: { id: string; productId: string; productName: string; qtyMilli: number; unitPriceCents: number; netCents: number }[];
  payments: { id: string; kind: 'deposit' | 'refund' | 'forfeit' | 'applied'; method: string; amountCents: number; reference: string | null; createdAt: string; userName: string | null }[];
  deliveries: { id: string; number: string; status: DeliveryStatus; scheduledDate: string; slot: keyof typeof DELIVERY_SLOTS }[];
}

const STATUS_TONE: Record<OrderStatus, 'gold' | 'ok' | 'neutral' | 'danger'> = { open: 'gold', ready: 'ok', completed: 'neutral', cancelled: 'danger' };
const methodLabel = (m: string) => PAYMENT_METHODS[m as PaymentMethod]?.label.split(' (')[0] ?? m;

export function OrdersPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [status, setStatus] = useState('active');
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['orders', status], queryFn: () => api.get<OrderRow[]>(`/api/orders${status ? `?status=${status}` : ''}`) });
  const held = (q.data ?? []).filter((o) => o.status === 'open' || o.status === 'ready').reduce((a, o) => a + o.paidCents, 0);
  return (
    <>
      <PageHeader
        title="Orders and lay-by"
        description="Goods a customer is paying off or waiting for. Deposits are held for the customer until the goods are handed over; stock leaves only then."
        actions={can('orders.manage') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>New order</Button>}
      />
      {status === 'active' && q.data && q.data.length > 0 && (
        <p className="mb-4 rounded-[8px] bg-paper px-4 py-3 text-sm">
          {q.data.length} order{q.data.length === 1 ? '' : 's'} open, holding <span className="font-semibold num">{formatMoney(held, cur)}</span> in customer deposits.
        </p>
      )}
      <Panel>
        <div className="border-b border-line px-5 py-3">
          <Select aria-label="Status" className="max-w-64" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Open and ready</option>
            <option value="open">{ORDER_STATUSES.open}</option>
            <option value="ready">{ORDER_STATUSES.ready}</option>
            <option value="completed">{ORDER_STATUSES.completed}</option>
            <option value="cancelled">{ORDER_STATUSES.cancelled}</option>
            <option value="">All orders</option>
          </Select>
        </div>
        {q.isLoading && <Spinner />}
        {q.error && <div className="p-4"><ErrorNotice error={q.error} /></div>}
        {q.data?.length === 0 && <EmptyState title="No orders here" />}
        {!!q.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Order</th>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total</th>
                  <th className="px-3 py-2.5 text-right font-medium">Paid</th>
                  <th className="px-5 py-2.5 text-right font-medium">Still owed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.map((o) => (
                  <tr key={o.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      <Link to={`/orders/${o.id}`} className="font-medium hover:underline">{o.number}</Link>
                      <span className="block text-xs text-muted">
                        {formatDateTime(o.createdAt)} — {o.itemCount} item{o.itemCount === 1 ? '' : 's'}
                        {o.dueDate ? ` — due ${o.dueDate}` : ''}
                      </span>
                    </td>
                    <td className="px-3 py-3">{o.customerName}</td>
                    <td className="px-3 py-3"><Badge tone={STATUS_TONE[o.status]}>{ORDER_STATUSES[o.status]}</Badge></td>
                    <td className="px-3 py-3 text-right num">{formatMoney(o.totalCents, cur)}</td>
                    <td className="px-3 py-3 text-right num">{formatMoney(o.paidCents, cur)}</td>
                    <td className="px-5 py-3 text-right num font-medium">{o.status === 'open' || o.status === 'ready' ? formatMoney(o.balanceCents, cur) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {creating && <NewOrderDialog onClose={() => setCreating(false)} />}
    </>
  );
}

interface DraftLine {
  key: string;
  product: ProductDto;
  qtyMilli: number | null;
}

function NewOrderDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [picking, setPicking] = useState(false);
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [dueDate, setDueDate] = useState(addDays(todayLocal(), 30));
  const [notes, setNotes] = useState('');
  const [deposit, setDeposit] = useState<{ amountCents: number | null; method: PaymentMethod; reference: string }>({ amountCents: null, method: 'cash', reference: '' });
  const total = lines.reduce((a, l) => a + lineTotal(l.product.priceCents, l.qtyMilli ?? 0), 0);
  const m = useMutation({
    mutationFn: () =>
      api.post<OrderDetail>('/api/orders', {
        branchId,
        customerId: customer!.id,
        dueDate: dueDate || null,
        notes,
        lines: lines.map((l) => ({ productId: l.product.id, qtyMilli: l.qtyMilli })),
        deposit: deposit.amountCents ? { method: deposit.method, amountCents: deposit.amountCents, reference: deposit.reference || null } : null,
      }),
    onSuccess: (o) => {
      toast(`${o.number} taken for ${o.customerName}`);
      for (const k of ['orders', 'current-shift', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      navigate(`/orders/${o.id}`);
    },
  });
  const f = fieldErrors(m.error);
  const ready = customer && branchId && lines.length && lines.every((l) => l.qtyMilli) && (deposit.amountCents ?? 0) <= total;
  return (
    <>
      <Dialog
        open={!picking}
        wide
        onClose={onClose}
        title="New order"
        description="For goods to be made, ordered in, collected later or paid off over time."
        footer={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!ready} busy={m.isPending} onClick={() => m.mutate()}>Take order</Button>
          </>
        }
      >
        <FormError error={m.error} />
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <p className="mb-1.5 text-sm font-medium">Customer</p>
            {customer ? (
              <div className="flex h-10 items-center justify-between rounded-[var(--radius-control)] border border-line-strong bg-paper px-3">
                <span className="truncate">{customer.name}{customer.phone ? <span className="text-sm text-muted"> — {customer.phone}</span> : null}</span>
                <button className="text-sm font-medium text-ledger-700 hover:underline" onClick={() => setPicking(true)}>Change</button>
              </div>
            ) : (
              <Button className="w-full" onClick={() => setPicking(true)}>Choose customer</Button>
            )}
          </div>
          <Field label="Branch" htmlFor="o-branch">
            <Select id="o-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </Field>
        </div>

        <div className="mt-5">
          <ProductPicker label="Items" branchId={branchId} onPick={(p) => setLines((ls) => (ls.some((l) => l.product.id === p.id) ? ls : [...ls, { key: p.id, product: p, qtyMilli: 1000 }]))} />
          {f.lines && <p className="mt-1 text-sm text-danger">{f.lines}</p>}
          {lines.length > 0 && (
            <ul className="mt-3 divide-y divide-line rounded-[8px] border border-line">
              {lines.map((l) => (
                <li key={l.key} className="grid grid-cols-[1fr_110px_110px_auto] items-center gap-3 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{l.product.name}</span>
                    <span className="block text-xs text-muted">
                      {formatMoney(l.product.priceCents, cur)} each{l.product.stock ? ` — ${formatQty(l.product.stock.qtyMilli)} in stock` : ''}
                    </span>
                  </span>
                  <QtyInput aria-label={`Quantity of ${l.product.name}`} unit={l.product.unit} value={l.qtyMilli} onChange={(q) => setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, qtyMilli: q } : x)))} />
                  <span className="text-right num">{formatMoney(lineTotal(l.product.priceCents, l.qtyMilli ?? 0), cur)}</span>
                  <button className="rounded-md p-1.5 text-muted hover:text-danger" aria-label={`Remove ${l.product.name}`} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                    <Trash2 className="size-4" />
                  </button>
                </li>
              ))}
              <li className="flex justify-between px-3 py-2.5 font-semibold">
                <span>Order total</span>
                <span className="num">{formatMoney(total, cur)}</span>
              </li>
            </ul>
          )}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Field label="Deposit now (optional)" htmlFor="o-dep" error={f.deposit ?? f['deposit.amountCents']}>
            <MoneyInput id="o-dep" currency={cur} value={deposit.amountCents} onChange={(c) => setDeposit({ ...deposit, amountCents: c })} invalid={(deposit.amountCents ?? 0) > total} />
          </Field>
          <Field label="Paid by" htmlFor="o-method">
            <Select id="o-method" value={deposit.method} onChange={(e) => setDeposit({ ...deposit, method: e.target.value as PaymentMethod })}>
              {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
            </Select>
          </Field>
          {PAYMENT_METHODS[deposit.method].needsReference ? (
            <Field label="Reference" htmlFor="o-ref" error={f.reference}>
              <TextInput id="o-ref" value={deposit.reference} onChange={(e) => setDeposit({ ...deposit, reference: e.target.value })} />
            </Field>
          ) : (
            <Field label="Ready or due by" htmlFor="o-due">
              <TextInput id="o-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          )}
          {PAYMENT_METHODS[deposit.method].needsReference && (
            <Field label="Ready or due by" htmlFor="o-due2">
              <TextInput id="o-due2" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          )}
          <Field label="Notes (optional)" htmlFor="o-notes" className="sm:col-span-3">
            <TextInput id="o-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Charcoal fabric, made to order — 3 weeks" />
          </Field>
        </div>
        {deposit.method === 'cash' && !!deposit.amountCents && <p className="mt-2 text-sm text-muted">Cash deposits go into your open till.</p>}
      </Dialog>
      {picking && (
        <CustomerPicker
          onClose={() => setPicking(false)}
          onPick={(c) => {
            setCustomer(c);
            setPicking(false);
          }}
        />
      )}
    </>
  );
}

export function OrderDetailPage() {
  const { id } = useParams();
  const { me, can, hasModule } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<'pay' | 'complete' | 'cancel' | 'deliver' | null>(null);
  const q = useQuery({ queryKey: ['order', id], queryFn: () => api.get<OrderDetail>(`/api/orders/${id}`) });
  const refresh = (o: OrderDetail) => {
    qc.setQueryData(['order', id], o);
    for (const k of ['orders', 'current-shift', 'dashboard', 'deliveries']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const ready = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/api/orders/${id}/ready`),
    onSuccess: (o) => {
      refresh(o);
      toast(`${o.number} marked ready`);
    },
  });
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  const o = q.data!;
  const live = o.status === 'open' || o.status === 'ready';
  const manage = can('orders.manage');
  return (
    <>
      <Link to="/orders" className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink"><ArrowLeft className="size-4" /> Orders</Link>
      <PageHeader
        title={`${o.number}: ${o.customerName}`}
        description={
          <>
            <Badge tone={STATUS_TONE[o.status]}>{ORDER_STATUSES[o.status]}</Badge>{' '}
            Taken {formatDateTime(o.createdAt)} at {o.branchName} by {o.createdByName ?? '—'}
            {o.dueDate && live ? `. Due ${o.dueDate}.` : '.'}
          </>
        }
        actions={
          <>
            {hasModule('deliveries') && can('deliveries.manage') && o.status !== 'cancelled' && (
              <Button icon={<Truck className="size-4" />} onClick={() => setDialog('deliver')}>Schedule delivery</Button>
            )}
            {manage && live && (
            <>
              <Button variant="danger" onClick={() => setDialog('cancel')}>Cancel order</Button>
              {o.status === 'open' && <Button busy={ready.isPending} onClick={() => ready.mutate()}>Mark ready</Button>}
              {o.balanceCents > 0 && <Button onClick={() => setDialog('pay')}>Take a payment</Button>}
              {can('pos.sell') && <Button variant="primary" onClick={() => setDialog('complete')}>Hand over</Button>}
            </>
            )}
          </>
        }
      />
      {ready.error && <div className="mb-4"><ErrorNotice error={ready.error} /></div>}
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <Panel title="Items">
            <table className="w-full text-left">
              <tbody className="divide-y divide-line">
                {o.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="px-5 py-2.5">
                      {l.productName}
                      <span className="block text-xs text-muted num">{formatQty(l.qtyMilli)} × {formatMoney(l.unitPriceCents, cur)}</span>
                    </td>
                    <td className="px-5 py-2.5 text-right num">{formatMoney(l.netCents, cur)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="px-5 py-3 font-semibold">Total</td>
                  <td className="px-5 py-3 text-right font-semibold num">{formatMoney(o.totalCents, cur)}</td>
                </tr>
              </tbody>
            </table>
            {o.notes && <p className="border-t border-line px-5 py-3 text-sm text-muted">{o.notes}</p>}
          </Panel>
          <Panel title="Payments">
            {o.payments.length === 0 ? (
              <EmptyState title="Nothing paid yet" />
            ) : (
              <table className="w-full text-left">
                <tbody className="divide-y divide-line">
                  {o.payments.map((p) => (
                    <tr key={p.id}>
                      <td className="px-5 py-2.5">
                        {p.kind === 'deposit' ? `Paid by ${methodLabel(p.method)}` : p.kind === 'refund' ? `Refunded by ${methodLabel(p.method)}` : p.kind === 'forfeit' ? 'Kept by the shop (cancelled)' : 'Applied to the sale'}
                        <span className="block text-xs text-muted">
                          {formatDateTime(p.createdAt)}{p.userName ? ` — ${p.userName}` : ''}{p.reference ? ` — ${p.reference}` : ''}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-right num">{formatMoney(p.amountCents, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel>
            <dl className="divide-y divide-line">
              <Row label="Order total" value={formatMoney(o.totalCents, cur)} />
              <Row label="Paid" value={formatMoney(o.paidCents, cur)} />
              {o.refundedCents > 0 && <Row label="Refunded" value={formatMoney(o.refundedCents, cur)} />}
              {o.forfeitedCents > 0 && <Row label="Deposit kept" value={formatMoney(o.forfeitedCents, cur)} />}
              {live && <Row label="Still owed" value={formatMoney(o.balanceCents, cur)} strong />}
            </dl>
            {o.saleId && (
              <p className="border-t border-line px-5 py-3 text-sm">
                Handed over as sale <Link className="font-medium text-ledger-700 hover:underline" to={`/sales/${o.saleId}`}>{o.saleNumber}</Link>
              </p>
            )}
            {o.cancelReason && <p className="border-t border-line px-5 py-3 text-sm text-muted">Cancelled: {o.cancelReason}</p>}
          </Panel>
          <Panel title="Customer">
            <div className="px-5 py-4 text-sm">
              <Link to={`/customers/${o.customerId}`} className="font-medium hover:underline">{o.customerName}</Link>
              {o.customerPhone && <p className="text-muted">{o.customerPhone}</p>}
              {o.customerAddress && <p className="text-muted">{o.customerAddress}</p>}
            </div>
          </Panel>
          {o.deliveries.length > 0 && (
            <Panel title="Deliveries">
              <ul className="divide-y divide-line">
                {o.deliveries.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <span>
                      <Link to={`/deliveries?date=${d.scheduledDate}`} className="font-medium hover:underline">{d.number}</Link>
                      <span className="block text-muted">{d.scheduledDate}, {DELIVERY_SLOTS[d.slot]}</span>
                    </span>
                    <Badge tone={d.status === 'delivered' ? 'ok' : d.status === 'failed' || d.status === 'cancelled' ? 'danger' : 'gold'}>{DELIVERY_STATUSES[d.status]}</Badge>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>
      {dialog === 'pay' && <PaymentDialog order={o} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'complete' && <CompleteDialog order={o} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'cancel' && <CancelDialog order={o} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'deliver' && (
        <DeliveryDialog
          order={o}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            void q.refetch();
          }}
        />
      )}
    </>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between px-5 py-3">
      <dt className={strong ? 'font-semibold' : 'text-muted'}>{label}</dt>
      <dd className={strong ? 'text-lg font-semibold num' : 'num'}>{value}</dd>
    </div>
  );
}

function PaymentDialog({ order, onClose, onDone }: { order: OrderDetail; onClose: () => void; onDone: (o: OrderDetail) => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const toast = useToast();
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/api/orders/${order.id}/payments`, { method, amountCents: amount, reference: reference || null }),
    onSuccess: (o) => {
      onDone(o);
      toast(`Payment recorded. ${formatMoney(o.balanceCents, cur)} still owed.`);
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Payment on ${order.number}`}
      description={`${formatMoney(order.balanceCents, cur)} still owed.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!amount || amount > order.balanceCents} busy={m.isPending} onClick={() => m.mutate()}>Record payment</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Amount" htmlFor="op-amt" error={f.amountCents}>
          <MoneyInput id="op-amt" autoFocus currency={cur} value={amount} onChange={setAmount} invalid={(amount ?? 0) > order.balanceCents} />
        </Field>
        <Field label="Paid by" htmlFor="op-method">
          <Select id="op-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
          </Select>
        </Field>
        {PAYMENT_METHODS[method].needsReference && (
          <Field label="Reference" htmlFor="op-ref" error={f.reference} className="sm:col-span-2">
            <TextInput id="op-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
      </div>
      <button className="mt-3 text-sm font-medium text-ledger-700 hover:underline" onClick={() => setAmount(order.balanceCents)}>Pay the full {formatMoney(order.balanceCents, cur)}</button>
    </Dialog>
  );
}

function CompleteDialog({ order, onClose, onDone }: { order: OrderDetail; onClose: () => void; onDone: (o: OrderDetail) => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const toast = useToast();
  const [clientRef] = useState(uuid);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [tendered, setTendered] = useState<number | null>(order.balanceCents || null);
  const [reference, setReference] = useState('');
  const due = order.balanceCents;
  const change = method === 'cash' && tendered != null ? tendered - due : 0;
  const m = useMutation({
    mutationFn: () =>
      api.post<OrderDetail>(`/api/orders/${order.id}/complete`, {
        clientRef,
        payments: due > 0 ? [{ method, currency: cur, amountCents: method === 'cash' ? tendered : due, reference: reference || null }] : [],
      }),
    onSuccess: (o) => {
      onDone(o);
      toast(`${o.number} handed over as ${o.saleNumber}${change > 0 ? `. Change ${formatMoney(change, cur)}` : ''}`);
      onClose();
    },
  });
  const okToGo = due === 0 || (method === 'cash' ? (tendered ?? 0) >= due : !PAYMENT_METHODS[method].needsReference || reference.trim().length > 0);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Hand over ${order.number}`}
      description={`Turns the order into a sale: the goods leave stock and the deposits are applied. Needs your open till at ${order.branchName}.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!okToGo} busy={m.isPending} onClick={() => m.mutate()}>Complete sale</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <dl className="mb-4 divide-y divide-line rounded-[8px] border border-line">
        <Row label="Order total" value={formatMoney(order.totalCents, cur)} />
        <Row label="Deposits applied" value={formatMoney(order.paidCents - order.refundedCents, cur)} />
        <Row label="To pay now" value={formatMoney(due, cur)} strong />
      </dl>
      {due > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Paid by" htmlFor="oc-method">
            <Select id="oc-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
            </Select>
          </Field>
          {method === 'cash' ? (
            <Field label="Cash handed over" htmlFor="oc-cash" hint={change > 0 ? `Change: ${formatMoney(change, cur)}` : undefined}>
              <MoneyInput id="oc-cash" currency={cur} value={tendered} onChange={setTendered} />
            </Field>
          ) : (
            PAYMENT_METHODS[method].needsReference && (
              <Field label="Reference" htmlFor="oc-ref">
                <TextInput id="oc-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
              </Field>
            )
          )}
        </div>
      )}
    </Dialog>
  );
}

function CancelDialog({ order, onClose, onDone }: { order: OrderDetail; onClose: () => void; onDone: (o: OrderDetail) => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const toast = useToast();
  const paid = order.paidCents - order.refundedCents;
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState<number | null>(paid || null);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const kept = paid - (refund ?? 0);
  const m = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/api/orders/${order.id}/cancel`, { reason, refundCents: refund ?? 0, refundMethod: (refund ?? 0) > 0 ? method : null, reference: reference || null }),
    onSuccess: (o) => {
      onDone(o);
      toast(`${o.number} cancelled`);
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Cancel ${order.number}?`}
      description={paid > 0 ? `${formatMoney(paid, cur)} has been paid. Give it back, or keep some as agreed in your lay-by terms.` : 'Nothing has been paid on this order.'}
      footer={
        <>
          <Button onClick={onClose}>Keep the order</Button>
          <Button variant="danger" disabled={reason.trim().length < 3 || (refund ?? 0) > paid} busy={m.isPending} onClick={() => m.mutate()}>Cancel order</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4">
        <Field label="Why is it cancelled?" htmlFor="ox-reason" error={f.reason}>
          <TextArea id="ox-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {paid > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Give back" htmlFor="ox-refund" error={f.refundCents} hint={kept > 0 ? `${formatMoney(kept, cur)} is kept and counted as income.` : 'The full amount is given back.'}>
              <MoneyInput id="ox-refund" currency={cur} value={refund} onChange={setRefund} invalid={(refund ?? 0) > paid} />
            </Field>
            {(refund ?? 0) > 0 && (
              <Field label="Given back by" htmlFor="ox-method" error={f.refundMethod}>
                <Select id="ox-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                  {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
                </Select>
              </Field>
            )}
            {(refund ?? 0) > 0 && PAYMENT_METHODS[method].needsReference && (
              <Field label="Reference" htmlFor="ox-ref" className="sm:col-span-2">
                <TextInput id="ox-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
              </Field>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
